import type { SwiperModule, SwiperOptions } from 'swiper/types';

import { Autoplay, FreeMode, Mousewheel, Navigation, Pagination, Zoom } from 'swiper/modules';

// should always be used when swiper items are clickable
const desktopDefaults: SwiperConfig = {
    longSwipesMs: 100, // Duration in which the mouse click has to be held down before it counts as long swipe
    longSwipesRatio: 0.25, // Ratio (percent) of a tile that has to be swiped before it counts as long swipe
    modules: [],
    shortSwipes: false, // Short swipes are all swipes shorter than long swipes
    threshold: 5, // No swipe will be executed when the mouse click is held down and moved this range of pixels
};

export const swiperModule: Record<SwiperModuleType, SwiperConfig> = {
    carousel: {
        autoplay: { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
        loop: true,
        modules: [Navigation, Autoplay],
        navigation: { nextEl: '.swiperNext', prevEl: '.swiperPrev' },
        slidesPerView: 4,
        spaceBetween: 10,
    },
    contentBoxSwiperDesktop: {
        ...desktopDefaults,
        direction: 'horizontal',
        freeMode: { enabled: true, sticky: true },
        modules: [Navigation, Pagination, Mousewheel, FreeMode],
        mousewheel: { enabled: true, forceToAxis: true },
        navigation: true,
        pagination: { enabled: false },
        resistance: false,
        spaceBetween: 10,
        watchSlidesProgress: true,
    },
    gallery: {
        modules: [Navigation, Pagination],
        navigation: true,
        pagination: { clickable: true, dynamicBullets: true },
    },
    galleryOverlay: {
        initialSlide: 0,
        loop: true,
        modules: [Navigation, Zoom],
        navigation: true,
        zoom: true,
    },
    galleryOverlayDesktopPreviewStripe: {
        ...desktopDefaults,
        centeredSlides: true,
        navigation: false,
        slidesPerView: 'auto',
        spaceBetween: 10,
        watchSlidesProgress: true,
    },
    galleryWithoutNavigation: {
        modules: [Pagination],
        navigation: false,
        pagination: { clickable: true, dynamicBullets: true },
    },
    map: {
        centeredSlides: true,
        modules: [],
        navigation: false,
        spaceBetween: 10,
    },
    tabView: {
        modules: [],
        navigation: false,
        spaceBetween: 10,
    },
};

export interface SwiperConfig {
    allowTouchMove?: SwiperOptions['allowTouchMove'];
    autoplay?: SwiperOptions['autoplay'];
    centeredSlides?: SwiperOptions['centeredSlides'];
    direction?: SwiperOptions['direction'];
    freeMode?: SwiperOptions['freeMode'];
    initialSlide?: SwiperOptions['initialSlide'];
    longSwipesMs?: SwiperOptions['longSwipesMs'];
    longSwipesRatio?: SwiperOptions['longSwipesRatio'];
    loop?: SwiperOptions['loop'];
    modules: SwiperModule[];
    mousewheel?: SwiperOptions['mousewheel'];
    navigation?: SwiperOptions['navigation'];
    pagination?: SwiperOptions['pagination'];
    resistance?: SwiperOptions['resistance'];
    resistanceRatio?: SwiperOptions['resistanceRatio'];
    shortSwipes?: SwiperOptions['shortSwipes'];
    slidesPerView?: SwiperOptions['slidesPerView'];
    spaceBetween?: SwiperOptions['spaceBetween'];
    speed?: SwiperOptions['speed'];
    threshold?: SwiperOptions['threshold'];
    watchSlidesProgress?: SwiperOptions['watchSlidesProgress'];
    zoom?: SwiperOptions['zoom'];
}

export type SwiperModuleType =
    | 'carousel'
    | 'contentBoxSwiperDesktop'
    | 'gallery'
    | 'galleryOverlay'
    | 'galleryOverlayDesktopPreviewStripe'
    | 'galleryWithoutNavigation'
    | 'map'
    | 'tabView';
